import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid, TextField, Button, MenuItem, Checkbox,
  FormControlLabel, Box, IconButton, Typography
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { useNavigate } from 'react-router-dom';

import dialogStyles from '../../../../components/dialog/GenericDialog.styles';
import ValidatedField from '../../../../components/inputs/ValidatedField';
import { OPERATOR_DATE_RULES } from '../../utils/operatorValidation';

import { useOperatorUnits } from '../../hooks/useOperatorUnits';

import {
  refranchiseOperator,
  fetchOperatorsLite,
} from '../../services/operator.api';

import useAuthUser from '../../../auth/hooks/useAuthUser';
import { formatName, generateFileName } from '../../utils/operators.helpers';

export default function RefranchiseOperatorTab({ initialData, onSubmit, helpers }) {
  const { close, setLoading } = helpers;
  const navigate = useNavigate();

  const { user } = useAuthUser();
  const displayUser = formatName(user?.name) || user?.email || 'Unknown User';

  const [operators, setOperators] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [filter, setFilter] = useState('');
  const [selectedUnits, setSelectedUnits] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchOperatorsLite()
      .then((ops) => mounted && setOperators(ops))
      .catch(() => mounted && setOperators([]));
    return () => { mounted = false; };
  }, []);

  const { units } = useOperatorUnits(initialData?.operator);

  useEffect(() => {
    setSelectedCode('');
    setEffectiveDate('');
    setSelectedUnits([]);
    setFilter('');
    setLoading(false);
  }, [setLoading, initialData?.operator]);

  const filtered = useMemo(
    () => units.filter((u) => u.name.toLowerCase().includes(filter.toLowerCase())),
    [units, filter]
  );

  const toggleUnit = (id) =>
    setSelectedUnits((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allSelected =
    filtered.length > 0 && filtered.every((u) => selectedUnits.includes(u.id));
  const toggleAll = () => setSelectedUnits(allSelected ? [] : filtered.map((u) => u.id));

  const maxDate = '2100-12-31';
  const validDate = effectiveDate && effectiveDate <= maxDate;
  const canSave = selectedCode && validDate && selectedUnits.length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    try {
      const filename = generateFileName();
      await refranchiseOperator({
        newOperator: selectedCode,
        effectiveFrom: effectiveDate,
        units: selectedUnits,
        filename,
        user: displayUser,
      });
      onSubmit?.();
      close();
      navigate('/home/structure-logs', { state: { fileName: filename, status: null } });
    } catch (err) {
      console.error('API error:', err);
      alert('Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Current Operator"
            fullWidth
            size="small"
            variant="standard"
            value={initialData.operator}
            InputProps={{ readOnly: true, disableUnderline: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="New Operator"
            select
            fullWidth
            size="small"
            variant="outlined"
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            sx={dialogStyles.outlinedField(Boolean(selectedCode))}
          >
            <MenuItem value="" disabled>Select new operator</MenuItem>
            {operators.map((op) => (
              <MenuItem key={op.code} value={op.code}>{op.name}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="Effective From"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            rules={OPERATOR_DATE_RULES}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Search Units"
            size="small"
            placeholder="Search units..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
            sx={dialogStyles.outlinedField(Boolean(filter))}
            InputProps={{
              endAdornment: (
                <IconButton onClick={toggleAll} size="small" sx={{ ml: 1, color: '#5236AB' }}>
                  {allSelected ? <ClearAllIcon /> : <DoneAllIcon />}
                </IconButton>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          {filtered.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#757575' }}>
              No units found.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 1,
                maxHeight: 260,
                overflowY: 'auto',
                pr: 1,
              }}
            >
              {filtered.map((u) => (
                <FormControlLabel
                  key={u.id}
                  control={
                    <Checkbox
                      checked={selectedUnits.includes(u.id)}
                      onChange={() => toggleUnit(u.id)}
                      size="small"
                    />
                  }
                  label={u.name}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: selectedUnits.includes(u.id) ? 'black' : '#757575',
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <Button onClick={close} variant="contained" sx={dialogStyles.btnSecondary}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          sx={dialogStyles.btnPrimary}
        >
          Save Changes
        </Button>
      </div>
    </>
  );
}
